import { z } from 'zod';
/**
 * Abonnements Recharge — `docs/specs-v2/06-abonnements-recharge.md`.
 *
 * Trois fonctions pilotées par webhook, chacune avec son mode (`06` §0). La
 * quatrième, l'export des abonnements actifs, ne fait que lire et n'a pas de
 * mode (`06` §4).
 */
/** Mode d'une fonction Recharge — `06` §0. */
export const RECHARGE_FEATURE_MODES = ['disabled', 'simulation', 'active'];
export const rechargeFeatureModeSchema = z.enum(RECHARGE_FEATURE_MODES);
export const RECHARGE_FEATURE_MODE = {
    disabled: {
        code: 'disabled',
        label: 'Désactivé',
        tone: 'gray',
        description: 'Les webhooks reçus sont ignorés.',
    },
    simulation: {
        code: 'simulation',
        label: 'Simulation',
        tone: 'orange',
        description: 'Flux calcule tout mais n’écrit rien chez Recharge et ne publie rien sur Slack.',
    },
    active: {
        code: 'active',
        label: 'Actif',
        tone: 'green',
        description: 'Flux écrit chez Recharge et publie sur Slack.',
    },
};
/** Fonctions Recharge pilotées par webhook — `06` §1.2, et `07` pour `renewalDiscount`. */
export const RECHARGE_FEATURES = ['chargeFailed', 'swissCanton', 'legacyMigration', 'renewalDiscount'];
export const rechargeFeatureSchema = z.enum(RECHARGE_FEATURES);
/** Topics Recharge écoutés par Flux, dans l'ordre de `06` §1.2. */
export const RECHARGE_TOPICS = [
    'charge/failed',
    'charge/max_retries_reached',
    'subscription/created',
    'address/created',
    'address/updated',
    'subscription/activated',
    'charge/created',
];
export const rechargeTopicSchema = z.enum(RECHARGE_TOPICS);
export const RECHARGE_FEATURE = {
    chargeFailed: {
        code: 'chargeFailed',
        label: 'Paiements échoués',
        description: 'Publie sur Slack chaque renouvellement refusé. N’écrit jamais chez Recharge.',
        topics: ['charge/failed', 'charge/max_retries_reached'],
    },
    swissCanton: {
        code: 'swissCanton',
        label: 'Canton suisse',
        description: 'Complète le canton des adresses suisses à partir du code postal.',
        topics: ['subscription/created', 'address/created', 'address/updated'],
    },
    legacyMigration: {
        code: 'legacyMigration',
        label: 'Migration legacy',
        description: 'Convertit en bundle natif un abonnement legacy réactivé par le client.',
        topics: ['subscription/activated'],
    },
    renewalDiscount: {
        code: 'renewalDiscount',
        label: 'Remise renouvellement',
        description: 'Pose la remise des opérations « remise sur le prochain renouvellement » sur les charges Recharge.',
        topics: ['charge/created'],
    },
};
/** Fonction qui traite un topic, `null` pour un topic que Flux n'écoute pas. */
export function rechargeFeatureForTopic(topic) {
    for (const feature of RECHARGE_FEATURES) {
        if (RECHARGE_FEATURE[feature].topics.includes(topic)) {
            return feature;
        }
    }
    return null;
}
/** Résultat du traitement d'un webhook Recharge — `06` §1.2, journal `06` §8.5. */
export const RECHARGE_WEBHOOK_OUTCOMES = ['pending', 'processed', 'simulated', 'ignored', 'duplicate', 'error'];
export const rechargeWebhookOutcomeSchema = z.enum(RECHARGE_WEBHOOK_OUTCOMES);
export const RECHARGE_WEBHOOK_OUTCOME = {
    pending: { code: 'pending', label: 'En attente', tone: 'gray' },
    processed: { code: 'processed', label: 'Traité', tone: 'green' },
    simulated: { code: 'simulated', label: 'Simulé', tone: 'orange' },
    ignored: { code: 'ignored', label: 'Ignoré', tone: 'gray' },
    duplicate: { code: 'duplicate', label: 'Doublon', tone: 'gray' },
    error: { code: 'error', label: 'Erreur', tone: 'red' },
};
/** Provenance d'un webhook enregistré chez Recharge, déduite de son URL (`06` §1.3). */
export const RECHARGE_WEBHOOK_ORIGINS = ['flux', 'legacy', 'other'];
export const rechargeWebhookOriginSchema = z.enum(RECHARGE_WEBHOOK_ORIGINS);
export const RECHARGE_WEBHOOK_ORIGIN = {
    flux: { code: 'flux', label: 'Flux', tone: 'blue' },
    legacy: { code: 'legacy', label: 'Ancien serveur', tone: 'orange' },
    other: { code: 'other', label: 'Autre', tone: 'gray' },
};
/** Statut d'une exécution longue (export, vérification des cantons) — `06` §6 `rechargeRuns`. */
export const RECHARGE_RUN_STATUSES = ['running', 'succeeded', 'failed'];
export const rechargeRunStatusSchema = z.enum(RECHARGE_RUN_STATUSES);
export const RECHARGE_RUN_STATUS = {
    running: { code: 'running', label: 'En cours', tone: 'blue' },
    succeeded: { code: 'succeeded', label: 'Terminé', tone: 'green' },
    failed: { code: 'failed', label: 'Échec', tone: 'red' },
};
/** URL du serveur Recharge d'Apotech que Flux remplace (`06` §10). */
export const RECHARGE_LEGACY_WEBHOOK_HOST = 'miumlab-recharge-webhooks.apps.apotechstudio.com';
/** Admin Recharge de Mium : liens « Ouvrir dans Recharge ». */
export const RECHARGE_ADMIN_URL = 'https://lesmiraculeux-sp.admin.rechargeapps.com/merchant';
/** Catégorie d'un paiement échoué, d'après `error_type` — `06` §2.1. */
export const CHARGE_FAILURE_CATEGORIES = ['customer_action', 'technical', 'unknown'];
export const chargeFailureCategorySchema = z.enum(CHARGE_FAILURE_CATEGORIES);
export const CHARGE_FAILURE_CATEGORY = {
    customer_action: {
        code: 'customer_action',
        label: 'Action client',
        tone: 'orange',
        description: 'Action client requise (carte/paiement)',
    },
    technical: { code: 'technical', label: 'Technique', tone: 'blue', description: 'Erreur technique / processeur' },
    unknown: { code: 'unknown', label: 'Inconnue', tone: 'gray', description: 'Cause inconnue' },
};
/** `charge/failed` → en relance ; `charge/max_retries_reached` → échec définitif (`06` §2.1). */
export const CHARGE_FAILURE_STATUSES = ['retrying', 'final'];
export const chargeFailureStatusSchema = z.enum(CHARGE_FAILURE_STATUSES);
export const CHARGE_FAILURE_STATUS = {
    retrying: { code: 'retrying', label: 'En relance', tone: 'orange' },
    final: { code: 'final', label: 'Échec définitif', tone: 'red' },
};
/** Résultat d'une correction de canton (`06` §3.2, §6 `rechargeCantonFixes`). */
export const CANTON_FIX_STATUSES = ['unresolved', 'updated', 'simulated', 'resolved_manually', 'error'];
export const cantonFixStatusSchema = z.enum(CANTON_FIX_STATUSES);
export const CANTON_FIX_STATUS = {
    unresolved: { code: 'unresolved', label: 'À traiter', tone: 'red', description: 'Canton introuvable sans deviner : à corriger dans Recharge.' },
    updated: { code: 'updated', label: 'Corrigée', tone: 'green' },
    simulated: { code: 'simulated', label: 'Simulée', tone: 'orange' },
    resolved_manually: { code: 'resolved_manually', label: 'Résolue à la main', tone: 'gray' },
    error: { code: 'error', label: 'Erreur', tone: 'red' },
};
/** Origine d'une correction : webhook Recharge ou vérification de toutes les adresses. */
export const CANTON_FIX_SOURCES = ['webhook', 'scan'];
export const cantonFixSourceSchema = z.enum(CANTON_FIX_SOURCES);
export const CANTON_FIX_SOURCE = {
    webhook: { code: 'webhook', label: 'Webhook', tone: 'gray' },
    scan: { code: 'scan', label: 'Vérification', tone: 'blue' },
};
/** Résultat d'une migration legacy (`06` §5, §6 `rechargeMigrations`). */
export const LEGACY_MIGRATION_STATUSES = ['migrated', 'simulated', 'failed', 'skipped', 'rolled_back'];
export const legacyMigrationStatusSchema = z.enum(LEGACY_MIGRATION_STATUSES);
export const LEGACY_MIGRATION_STATUS = {
    migrated: { code: 'migrated', label: 'Migré', tone: 'green' },
    simulated: { code: 'simulated', label: 'Simulé', tone: 'orange' },
    failed: { code: 'failed', label: 'Échec', tone: 'red' },
    skipped: { code: 'skipped', label: 'Ignoré', tone: 'gray' },
    rolled_back: {
        code: 'rolled_back',
        label: 'Annulée',
        tone: 'gray',
        description: 'Abonnement remis dans son état d’origine depuis Flux.',
    },
};
/**
 * Catégorie d'abonné poussée sur les profils Klaviyo des abonnés actifs
 * (`06` §11) : `legacy` si tous ses abonnements actifs sont legacy,
 * `legacy_new` s'il en a au moins un de chaque sorte, `new` si aucun n'est legacy.
 */
export const KLAVIYO_SUBSCRIBER_CATEGORIES = ['legacy', 'legacy_new', 'new'];
export const klaviyoSubscriberCategorySchema = z.enum(KLAVIYO_SUBSCRIBER_CATEGORIES);
/** Propriété de profil Klaviyo écrite par Flux, et seulement par Flux. */
export const KLAVIYO_CATEGORY_PROPERTY = 'rc_subscription_mium_category';
export const KLAVIYO_SUBSCRIBER_CATEGORY = {
    legacy: { code: 'legacy', label: 'Legacy', tone: 'orange', description: 'Tous ses abonnements actifs sont legacy.' },
    legacy_new: {
        code: 'legacy_new',
        label: 'Legacy + nouveau',
        tone: 'blue',
        description: 'Au moins un abonnement actif legacy et au moins un qui ne l’est pas.',
    },
    new: { code: 'new', label: 'Nouveau', tone: 'green', description: 'Aucun de ses abonnements actifs n’est legacy.' },
};
/** Changement d'un profil Klaviyo lors d'une synchronisation (`06` §11). */
export const KLAVIYO_SYNC_ACTIONS = ['set', 'unset'];
export const klaviyoSyncActionSchema = z.enum(KLAVIYO_SYNC_ACTIONS);
export const KLAVIYO_SYNC_ACTION = {
    set: { code: 'set', label: 'Catégorie posée', tone: 'green' },
    unset: { code: 'unset', label: 'Catégorie retirée', tone: 'gray' },
};
//# sourceMappingURL=recharge.js.map